import { useEffect, useState } from 'react';
import { Button, Select, MenuItem, FormControl, InputLabel, TextField, Chip, Divider, IconButton } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { FieldProps } from '@rjsf/utils';
import { Condition, ConditionGroup, Operator } from 'api/model';
import { NodeColumnsInfo } from 'api/workflows-steps/formSchema';
import ColumnSelectorButton from './ColumnSelectorButton';
import { iconButtonPillStyle } from 'components/button/iconButtonPillStyle';
import { ConditionBox, GroupBox, OperatorToggle, QueryCard } from './QueryBuilderField.styles';

const initialGroup: ConditionGroup = {
  operator: 'AND',
  conditions: [],
  groups: [],
};

const OPERATORS_WITH_VALUE = ['=', '!=', '>', '<', '>=', '<=', 'CONTAINS', 'NOT CONTAINS', 'STARTS WITH', 'ENDS WITH'];
const OPERATORS_WITH_LIST = ['IN', 'NOT IN'];
const OPERATORS_WITHOUT_VALUE = ['IS NULL', 'IS NOT NULL'];
const ALL_OPERATORS = [...OPERATORS_WITH_VALUE, ...OPERATORS_WITH_LIST, ...OPERATORS_WITHOUT_VALUE];

type QueryBuilderFieldOptions = {
  columns?: NodeColumnsInfo[];
};

const QueryBuilderField = (props: FieldProps) => {
  const [data, setData] = useState<ConditionGroup>(props.formData || initialGroup);
  const options = (props.uiSchema?.['ui:options'] ?? {}) as QueryBuilderFieldOptions;
  const nodeColumns = options.columns ?? [];

  useEffect(() => {
    props.onChange(data);
  }, [data]);

  const getGroup = (root: ConditionGroup, path: number[]): ConditionGroup => {
    let current = root;
    for (const index of path) {
      current = current.groups[index];
    }
    return current;
  };

  const addCondition = (groupPath: number[]) => {
    setData((prev) => {
      const tempData = JSON.parse(JSON.stringify(prev));
      const targetGroup = getGroup(tempData, groupPath);
      targetGroup.conditions.push({
        column_name: '',
        operator: '=',
        filter_value: '',
      });
      return { ...tempData };
    });
  };

  const removeCondition = (groupPath: number[], conditionIndex: number): void => {
    setData((prev) => {
      const tempData = JSON.parse(JSON.stringify(prev));
      const targetGroup = getGroup(tempData, groupPath);
      targetGroup.conditions.splice(conditionIndex, 1);
      return { ...tempData };
    });
  };

  const updateCondition = (groupPath: number[], conditionIndex: number, updates: Partial<Condition>) => {
    setData((prev) => {
      const tempData = JSON.parse(JSON.stringify(prev));
      const targetGroup = getGroup(tempData, groupPath);
      const condition = targetGroup.conditions[conditionIndex];
      Object.assign(condition, updates);
      return { ...tempData };
    });
  };

  const addGroup = (parentGroupPath: number[]) => {
    setData((prev) => {
      const tempData = JSON.parse(JSON.stringify(prev));
      const targetGroup = getGroup(tempData, parentGroupPath);
      targetGroup.groups.push({
        operator: 'AND',
        conditions: [],
        groups: [],
      });
      return { ...tempData };
    });
  };

  const removeGroup = (parentGroupPath: number[], groupIndex: number) => {
    setData((prev) => {
      const tempData = JSON.parse(JSON.stringify(prev));
      const targetGroup = getGroup(tempData, parentGroupPath);
      targetGroup.groups.splice(groupIndex, 1);
      return { ...tempData };
    });
  };

  const updateGroupOperator = (groupPath: number[], operator: 'AND' | 'OR') => {
    setData((prev) => {
      const tempData = JSON.parse(JSON.stringify(prev));
      const targetGroup = getGroup(tempData, groupPath);
      targetGroup.operator = operator;
      return { ...tempData };
    });
  };

  const addListValue = (groupPath: number[], conditionIndex: number) => {
    setData((prev) => {
      const tempData = JSON.parse(JSON.stringify(prev));
      const targetGroup = getGroup(tempData, groupPath);
      const condition = targetGroup.conditions[conditionIndex];
      if (!condition.in_values) condition.in_values = [''];
      condition.in_values.push('');
      return { ...tempData };
    });
  };

  const updateListValue = (groupPath: number[], conditionIndex: number, valueIndex: number, newVal: string) => {
    setData((prev) => {
      const tempData = JSON.parse(JSON.stringify(prev));
      const targetGroup = getGroup(tempData, groupPath);
      const condition = targetGroup.conditions[conditionIndex];
      if (!condition.in_values) condition.in_values = [''];
      condition.in_values[valueIndex] = newVal;
      return { ...tempData };
    });
  };

  const removeListValue = (groupPath: number[], conditionIndex: number, valueIndex: number) => {
    setData((prev) => {
      const tempData = JSON.parse(JSON.stringify(prev));
      const targetGroup = getGroup(tempData, groupPath);
      const condition = targetGroup.conditions[conditionIndex];
      if (condition.in_values) {
        condition.in_values.splice(valueIndex, 1);
        // Ensure at least one value remains
        if (condition.in_values.length === 0) {
          condition.in_values.push('');
        }
      }
      return { ...tempData };
    });
  };

  const renderValueField = (condition: Condition, groupPath: number[], conditionIndex: number) => {
    if (OPERATORS_WITH_LIST.includes(condition.operator)) {
      const values = condition.in_values || [''];
      return (
        <div className="flex-1 min-w-28 flex flex-col gap-2">
          {values.map((val, idx) => (
            <div key={idx} className="flex items-center gap-1">
              <TextField
                size="small"
                value={val}
                onChange={(e) => updateListValue(groupPath, conditionIndex, idx, e.target.value)}
                placeholder="Enter value"
                disabled={props.disabled}
              />
              {values.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => removeListValue(groupPath, conditionIndex, idx)}
                  disabled={props.disabled}
                  sx={iconButtonPillStyle}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </div>
          ))}
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => addListValue(groupPath, conditionIndex)}
            disabled={props.disabled}
          >
            Value
          </Button>
        </div>
      );
    }

    const isNoValueOperator = OPERATORS_WITHOUT_VALUE.includes(condition.operator);

    return (
      <TextField
        label="Filter value"
        disabled={isNoValueOperator || props.disabled}
        value={isNoValueOperator ? '' : condition.filter_value || ''}
        onChange={(e) => updateCondition(groupPath, conditionIndex, { filter_value: e.target.value })}
        className="flex-1 min-w-28"
        placeholder="Enter value"
      />
    );
  };

  const renderCondition = (condition: Condition, groupPath: number[], conditionIndex: number) => (
    <ConditionBox key={conditionIndex} className="flex items-center gap-4 p-4 rounded-xl">
      <TextField
        label="Column name"
        value={condition.column_name || ''}
        onChange={(e) => updateCondition(groupPath, conditionIndex, { column_name: e.target.value })}
        className="flex-1 min-w-28"
        placeholder="Enter column name"
        disabled={props.disabled}
      />

      <FormControl className="min-w-40">
        <InputLabel>Operator</InputLabel>
        <Select
          value={condition.operator || ''}
          label="Operator"
          onChange={(e) => updateCondition(groupPath, conditionIndex, { operator: e.target.value as Operator })}
          disabled={props.disabled}
        >
          {ALL_OPERATORS.map((operator) => (
            <MenuItem key={operator} value={operator}>
              {operator}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {renderValueField(condition, groupPath, conditionIndex)}

      <ColumnSelectorButton
        className="grow-0"
        columns={nodeColumns}
        onSelect={(columnName) => updateCondition(groupPath, conditionIndex, { column_name: columnName })}
        disabled={props.disabled}
      />

      <IconButton
        className="grow-0"
        size="small"
        onClick={() => removeCondition(groupPath, conditionIndex)}
        disabled={props.disabled}
        sx={iconButtonPillStyle}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </ConditionBox>
  );

  const renderGroup = (group: ConditionGroup, groupPath: number[], isRoot: boolean = false) => {
    return (
      <GroupBox key={groupPath.join('-')} isRoot={isRoot} className={`${isRoot ? 'space-y-4' : 'ml-5 pl-4 space-y-4'}`}>
        <div className="flex items-center gap-3">
          <FormControl size="small">
            <OperatorToggle
              value={group.operator}
              selected
              onChange={() => updateGroupOperator(groupPath, group.operator === 'AND' ? 'OR' : 'AND')}
              size="small"
              className="w-12 font-bold"
              disabled={props.disabled}
            >
              {group.operator}
            </OperatorToggle>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => addCondition(groupPath)}
            disabled={props.disabled}
          >
            Condition
          </Button>

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => addGroup(groupPath)}
            disabled={props.disabled}
          >
            Group
          </Button>

          {!isRoot && (
            <IconButton
              size="small"
              onClick={() => removeGroup(groupPath.slice(0, -1), groupPath[groupPath.length - 1])}
              disabled={props.disabled}
              sx={iconButtonPillStyle}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </div>
        {(group.conditions.length > 0 || group.groups.length > 0) && (
          <div className="space-y-3">
            {group.conditions.map((condition, index) => renderCondition(condition, groupPath, index))}
            {group.conditions.length > 0 && group.groups.length > 0 && (
              <div className="flex items-center gap-3 my-4">
                <Divider className="flex-1" />
                <Chip label={group.operator} size="small" color="primary" className="font-medium" />
                <Divider className="flex-1" />
              </div>
            )}
            {group.groups.map((subGroup, index) => renderGroup(subGroup, [...groupPath, index]))}
          </div>
        )}
      </GroupBox>
    );
  };

  return (
    <QueryCard variant="outlined" className="p-6 overflow-auto">
      {renderGroup(data, [], true)}
    </QueryCard>
  );
};

export default QueryBuilderField;
